import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { Button, ThemeToggle } from '../ui';
import './Header.css';

export const Header: React.FC = () => {
  const { isAuthenticated, user, signOut } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePage = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <header className="header" role="banner">
      <div className="header__container">
        {/* Logo/Brand */}
        <div className="header__brand">
          <Link to="/" className="header__logo">
            <span className="header__logo-icon">👨‍💻</span>
            <span className="header__logo-text">My Nerdy Son</span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="header__nav" role="navigation" aria-label="Main navigation">
          <ul className="header__nav-list">
            {isAuthenticated ? (
              <>
                <li className="header__nav-item">
                  <Link 
                    to="/chat" 
                    className={`header__nav-link ${isActivePage('/chat') ? 'header__nav-link--active' : ''}`}
                  >
                    Chat
                  </Link>
                </li>
                <li className="header__nav-item">
                  <Link 
                    to="/knowledge-base" 
                    className={`header__nav-link ${isActivePage('/knowledge-base') ? 'header__nav-link--active' : ''}`}
                  >
                    Help Topics
                  </Link>
                </li>
                <li className="header__nav-item">
                  <Link 
                    to="/settings" 
                    className={`header__nav-link ${isActivePage('/settings') ? 'header__nav-link--active' : ''}`}
                  >
                    Settings
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li className="header__nav-item">
                  <Link 
                    to="/auth/signin" 
                    className={`header__nav-link ${isActivePage('/auth/signin') ? 'header__nav-link--active' : ''}`}
                  >
                    Sign In
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>

        {/* User Menu */}
        <div className="header__user">
          <div className="header__theme-toggle">
            <ThemeToggle showLabel={false} />
          </div>
          {isAuthenticated ? (
            <div className="header__user-menu">
              <span className="header__user-greeting">
                Hello{user?.givenName ? `, ${user.givenName}` : ''}!
              </span>
              <Button variant="outline" onClick={handleSignOut}>
                Sign Out
              </Button>
            </div>
          ) : (
            <div className="header__auth-buttons">
              <Link to="/auth/signup">
                <Button variant="primary">Get Started</Button>
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="header__mobile-toggle"
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label="Toggle navigation menu"
        >
          <span className="header__mobile-toggle-icon">
            {isMobileMenuOpen ? '✕' : '☰'}
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div id="mobile-menu" className="header__mobile-menu">
          <nav className="header__mobile-nav" role="navigation" aria-label="Mobile navigation">
            <ul className="header__mobile-nav-list">
              {isAuthenticated ? (
                <>
                  <li className="header__mobile-nav-item">
                    <Link 
                      to="/chat" 
                      className="header__mobile-nav-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Chat
                    </Link>
                  </li>
                  <li className="header__mobile-nav-item">
                    <Link 
                      to="/knowledge-base" 
                      className="header__mobile-nav-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Help Topics
                    </Link>
                  </li>
                  <li className="header__mobile-nav-item">
                    <Link 
                      to="/settings" 
                      className="header__mobile-nav-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Settings
                    </Link>
                  </li>
                  <li className="header__mobile-nav-item">
                    <div className="header__mobile-theme-toggle">
                      <ThemeToggle showLabel={true} />
                    </div>
                  </li>
                  <li className="header__mobile-nav-item">
                    <button 
                      className="header__mobile-nav-link header__mobile-nav-button"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleSignOut();
                      }}
                    >
                      Sign Out
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li className="header__mobile-nav-item">
                    <Link 
                      to="/auth/signin" 
                      className="header__mobile-nav-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                  </li>
                  <li className="header__mobile-nav-item">
                    <Link 
                      to="/auth/signup" 
                      className="header__mobile-nav-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Get Started
                    </Link>
                  </li>
                  <li className="header__mobile-nav-item">
                    <div className="header__mobile-theme-toggle">
                      <ThemeToggle showLabel={true} />
                    </div>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};